import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { MK_DEFAULTS, MK_SECTIONS, fieldLabel } from "../../lib/marketingDefaults";
import MediaInput from "../../components/admin/MediaInput";

const SLUGS = ["louis-vuitton", "zomato", "apple", "nike"];

export default function MarketingAdmin() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [openSection, setOpenSection] = useState("hero");

  useEffect(() => {
    api.get("/marketing").then(({ data }) => {
      const safe = {
        brand_logo_url: "",
        brand_logo_visible: true,
        brand_logo_monochrome: false,
        ...MK_DEFAULTS,
        ...(data || {}),
      };
      const byslug = Object.fromEntries((safe.brand_examples || []).map((b) => [b.slug, b]));
      safe.brand_examples = SLUGS.map(
        (slug) =>
          byslug[slug] || {
            slug,
            name: slug.split("-").map((w) => w[0].toUpperCase() + w.slice(1)).join(" "),
            body: "",
            tail: "",
            logo_url: "",
            logo_visible: true,
            monochrome: true,
          },
      );
      setData(safe);
    });
  }, []);

  if (!data) return <p className="text-[#8A8A93]">Loading…</p>;

  const set = (patch) => setData((d) => ({ ...d, ...patch }));
  const setExample = (idx, patch) =>
    setData((d) => ({
      ...d,
      brand_examples: d.brand_examples.map((b, i) => (i === idx ? { ...b, ...patch } : b)),
    }));

  const save = async () => {
    setBusy(true);
    setMsg("");
    try {
      await api.put("/marketing", data);
      setMsg("Saved.");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setMsg("Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-marketing" className="max-w-4xl space-y-10">
      <header>
        <p className="overline text-[#C9A050]/70 mb-3">Marketing page</p>
        <h1 className="font-display text-3xl md:text-4xl tracking-tight">Edit /marketing</h1>
        <p className="text-[#8A8A93] text-sm mt-2 max-w-2xl">
          Every line on the public marketing page is editable below, organised by section.
          Brand basics + 4 brand examples have logo upload + visibility / monochrome toggles.
          Typography is controlled separately under the Typography tab.
        </p>
      </header>

      {/* Brand basics — Sim's Glow Cream */}
      <section className="border border-white/10 bg-white/[0.02] rounded-xl p-6 md:p-8 space-y-5">
        <h2 className="font-display text-2xl">Sim's Glow Cream — brand logo</h2>
        <div>
          <label className="overline block mb-2">Logo</label>
          <MediaInput
            value={data.brand_logo_url || ""}
            onChange={(v) => set({ brand_logo_url: v })}
            accept="image"
            folder="marketing"
            testid="mk-brand-logo-url"
            placeholder="https://…"
          />
        </div>
        <Toggle label="Show logo on /marketing" checked={data.brand_logo_visible} onChange={(v) => set({ brand_logo_visible: v })} testid="mk-brand-logo-visible" />
        <Toggle label="Monochrome (gold-tone treatment)" checked={data.brand_logo_monochrome} onChange={(v) => set({ brand_logo_monochrome: v })} testid="mk-brand-logo-mono" />
        {data.brand_logo_url && (
          <div className="pt-2">
            <p className="overline text-[#8A8A93] mb-2">Preview</p>
            <img src={data.brand_logo_url} alt="" className={`h-14 object-contain ${data.brand_logo_monochrome ? "mk-logo-mono" : ""}`} />
          </div>
        )}
      </section>

      {/* Content fields — accordion by section */}
      <section className="space-y-3">
        <h2 className="font-display text-2xl mb-4">Page content (every line, editable)</h2>
        {MK_SECTIONS.map((sec) => {
          const isOpen = openSection === sec.id;
          return (
            <div key={sec.id} data-testid={`mk-section-${sec.id}`} className="border border-white/10 rounded-xl bg-white/[0.02] overflow-hidden">
              <button
                type="button"
                onClick={() => setOpenSection(isOpen ? "" : sec.id)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-white/[0.03] transition-colors"
              >
                <span className="font-display text-xl">{sec.label}</span>
                <span className={`text-[#C9A050] transition-transform ${isOpen ? "rotate-180" : ""}`}>▾</span>
              </button>
              {isOpen && (
                <div className="px-6 pb-6 pt-2 space-y-4 border-t border-white/5">
                  {sec.id === "highlights" && (
                    <p className="text-xs text-[#8A8A93] leading-relaxed">
                      Comma-separated words/phrases. Matches are case-insensitive and whole-word
                      only (so &ldquo;art&rdquo; won&rsquo;t match inside &ldquo;artistry&rdquo;).
                      Headings are skipped — only body copy is styled. Common words like
                      &ldquo;the&rdquo; will be highlighted everywhere; prefer brand and concept
                      terms.
                    </p>
                  )}
                  {sec.fields.map((fid) => (
                    <FieldEditor
                      key={fid}
                      id={fid}
                      label={fieldLabel(fid)}
                      value={data[fid] ?? ""}
                      placeholder={highlightPlaceholder(fid) || MK_DEFAULTS[fid] || ""}
                      multiline={fid === "gold_words" || fid === "underline_words"}
                      onChange={(v) => set({ [fid]: v })}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </section>

      {/* Brand examples (Section 04) — full editor + logo controls */}
      <section className="space-y-5">
        <h2 className="font-display text-2xl">Section 04 · Brand example logos</h2>
        {data.brand_examples.map((b, i) => (
          <details key={b.slug} data-testid={`mk-example-${b.slug}`} className="border border-white/10 bg-white/[0.02] rounded-xl group" open={i === 0}>
            <summary className="px-6 py-4 flex items-center justify-between gap-4 cursor-pointer">
              <span className="flex items-center gap-3">
                {b.logo_visible && b.logo_url && (
                  <img src={b.logo_url} alt="" className={`h-6 w-auto object-contain ${b.monochrome ? "mk-logo-mono" : ""}`} />
                )}
                <span className="font-display text-xl">{b.name || b.slug}</span>
              </span>
              <span className="overline text-[#8A8A93]">{b.slug}</span>
            </summary>
            <div className="px-6 pb-6 pt-2 space-y-4 border-t border-white/5">
              <FieldEditor id={`name-${b.slug}`} label="Display name" value={b.name} placeholder={b.slug} onChange={(v) => setExample(i, { name: v })} />
              <FieldEditor id={`body-${b.slug}`} label="Body" value={b.body} placeholder="Description…" onChange={(v) => setExample(i, { body: v })} multiline />
              <FieldEditor id={`tail-${b.slug}`} label="Tail (optional)" value={b.tail} placeholder="" onChange={(v) => setExample(i, { tail: v })} />
              <div>
                <label className="overline block mb-2">Logo</label>
                <MediaInput
                  value={b.logo_url || ""}
                  onChange={(v) => setExample(i, { logo_url: v })}
                  accept="image"
                  folder="marketing"
                  testid={`mk-ex-logo-${b.slug}`}
                  placeholder="https://…"
                  compact
                />
              </div>
              <div className="flex items-center gap-8 flex-wrap">
                <Toggle label="Show logo" checked={b.logo_visible} onChange={(v) => setExample(i, { logo_visible: v })} testid={`mk-ex-show-${b.slug}`} />
                <Toggle label="Monochrome" checked={b.monochrome} onChange={(v) => setExample(i, { monochrome: v })} testid={`mk-ex-mono-${b.slug}`} />
              </div>
            </div>
          </details>
        ))}
      </section>

      {/* Save bar */}
      <div className="sticky bottom-0 bg-[#0A0A0C]/92 backdrop-blur-md py-4 border-t border-white/5 flex items-center gap-4 z-10">
        <button onClick={save} disabled={busy} data-testid="mk-save" className="overline border border-[#C9A050] text-[#C9A050] hover:bg-[#C9A050] hover:text-black px-6 py-3 transition-all disabled:opacity-40">
          {busy ? "Saving…" : "Save changes"}
        </button>
        {msg && <span className="text-[#C9A050] text-sm" data-testid="mk-save-msg">{msg}</span>}
      </div>
    </div>
  );
}

/* ---------- field components ---------- */

function highlightPlaceholder(fid) {
  if (fid === "gold_words") return "value, audience, Sim's Glow Cream, perception";
  if (fid === "underline_words") return "specific, every brand we work with";
  return "";
}

function FieldEditor({ id, label, value, placeholder, onChange, multiline }) {
  // Multi-line for long fields automatically, single-line otherwise
  const auto = (value || "").length > 60 || (value || "").includes("\n") || multiline;
  return (
    <div>
      <label className="block text-xs text-[#8A8A93] mb-1.5 tracking-wide" htmlFor={`f-${id}`}>{label}</label>
      {auto ? (
        <textarea
          id={`f-${id}`}
          rows={Math.max(2, Math.min(6, ((value || "").match(/\n/g) || []).length + 2))}
          value={value || ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`mk-field-${id}`}
          className="w-full bg-transparent border border-white/10 focus:border-[#C9A050]/60 rounded px-3 py-2 outline-none transition-colors resize-y min-h-[64px] text-sm"
        />
      ) : (
        <input
          id={`f-${id}`}
          type="text"
          value={value || ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`mk-field-${id}`}
          className="w-full bg-transparent border-b border-white/15 focus:border-[#C9A050] py-2 outline-none transition-colors text-sm"
        />
      )}
    </div>
  );
}

function UrlField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="overline block mb-2">{label}</label>
      <input
        type="url"
        value={value || ""}
        placeholder="https://…"
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-transparent border-b border-white/15 focus:border-[#C9A050] py-2 outline-none transition-colors"
      />
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="inline-flex items-center gap-3 cursor-pointer select-none">
      <span className={`relative w-10 h-5 rounded-full transition-colors ${checked ? "bg-[#C9A050]" : "bg-white/15"}`}>
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : ""}`} />
        <input type="checkbox" className="sr-only" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
      </span>
      <span className="text-sm">{label}</span>
    </label>
  );
}
